import React, { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Field, FieldArray, Formik } from 'formik'
import * as Yup from 'yup'
import Alert from 'react-s-alert'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { DialogContent, DialogOverlay } from '@reach/dialog'
import { transparentize } from 'polished'
import { space as styledSpace } from 'styled-system'
import ClipLoader from 'react-spinners/ClipLoader'
import { fontSizes, radius, space } from 'theme'
import { ERROR_MESSAGE } from 'consts'
import { initializeArrayWithRange } from 'helpers'
import request from 'utils/request'
import errorHelper from 'utils/errorHelper'
import { H4, Text } from 'components/atoms/Typography'
import { Flex, Box, Grid } from 'components/atoms/Layout'
import Button from 'components/atoms/Button'
import Image from 'components/atoms/Image'
import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import Poll from 'components/atoms/Poll'
import ErrorLabel from 'components/atoms/ErrorLabel'
import Icon from 'components/atoms/Icon'
import ButtonWithLoading from 'components/molecules/ButtonWithLoading'
import Counter from 'components/molecules/Counter'
import Tooltip from 'components/molecules/Tooltip'
import { ROUTE_MEDIA } from 'routes/Calendar/consts'
import {
  getSelectedProfileGroupsAndTimezone,
  transformSecondsToDuration,
  transformDurationToSeconds,
} from 'routes/Calendar/helpers'

const StyledDialogOverlay = styled(DialogOverlay)`
  &&& {
    background-color: ${({ theme }) => transparentize(0.2, theme.colors.background_modal_overlay)};
    z-index: 2147483001;
  }
`

const StyledDialogContent = styled(DialogContent)`
  &&& {
    background-color: ${({ theme }) => theme.colors.background_card};
    position: relative;
    max-width: 510px;
    width: 100%;
    top: 50%;
    transform: translate(0, -50%);
    padding: 0;
    border-radius: ${radius.l};
    ${styledSpace};
    margin: 0 auto;
    height: 100%;
    display: flex;
    flex-direction: column;
    max-height: 780px;
  }
`

const StyledDialogEnvironmentWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.background_modal_header};
  ${({ $isTop }) => $isTop && `border-radius: ${radius.l} ${radius.l} 0 0;`}
  ${({ $isBottom }) => $isBottom && `border-radius: 0 0 ${radius.l} ${radius.l};`}
`

const CloseIconWrapper = styled(Box)`
  position: absolute;
  top: -6px;
  right: -9px;
  background: ${({ theme }) => theme.colors.background_card};
  height: 20px;
  width: 20px;
  border-radius: ${radius.pill};
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledAddPollOptionWrapper = styled(Flex)`
  cursor: pointer;
`

const StyledPollOptionWrapper = styled(Flex)`
  box-shadow: 0 0 1px 0 ${({ theme }) => transparentize(0.7, theme.colors.box_shadow)};
  padding: ${space.m};
  flex-direction: column;
  width: 100%;
  margin-bottom: ${space.m};
  border-radius: ${radius.l};
  position: relative;
`

const NavigationLinkIconWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.background_card};
  height: 20px;
  width: 20px;
  border-radius: ${radius.pill};
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 0 4px ${({ theme }) => transparentize(0.7, theme.colors.box_shadow)};
  z-index: 2;
  position: absolute;
  top: -9px;
  right: -9px;
`

const StyledSelect = styled(Select)`
  padding: 0 ${space.xs};
  font-size: ${fontSizes.xs};
  background-position: calc(100% - ${space.s}) center;
  ${({ $error, theme }) => $error && `border-color: ${theme.colors.error}`}
`

const InfoIcon = styled(Icon.Info)`
  fill: ${({ theme }) => theme.colors.secondaryText};
  cursor: pointer;
`

const { TITLE, DESCRIPTION, OPTIONS, DURATION, DURATION_IN_SECONDS, DAYS, HOURS, MINUTES } = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  OPTIONS: 'options',
  DURATION: 'duration',
  DURATION_IN_SECONDS: 'duration_in_seconds',
  DAYS: 'days',
  HOURS: 'hours',
  MINUTES: 'minutes',
}

const MAX_POLL_TITLE_LENGTH = 140
const MAX_POLL_DESCRIPTION_LENGTH = 500
const MAX_POLL_OPTION_TEXT_LENGTH = 25
const MIN_POLL_OPTIONS = 2
const MAX_POLL_OPTIONS = 4

const THREE_DAYS = 259200

const DAYS_OPTIONS = initializeArrayWithRange(7, 0)
const HOURS_OPTIONS = initializeArrayWithRange(24, 0)
const MINUTES_OPTIONS = initializeArrayWithRange(59, 0)

const DURATION_HELP_LINK = ''

const PollModal = ({ user, data, postText, isOpen, handleDismiss, handleUpdateMedias, folderPath }) => {
  const formRef = useRef(null)

  const [poll, setPoll] = useState({})
  const [isSavingPoll, setIsSavingPoll] = useState(false)
  const [isCheckingPollEditAvailability, setIsCheckingPollEditAvailability] = useState(true)
  const [canPollBeEdited, setCanPollBeEdited] = useState(true)

  const { id, title, description, options, duration, duration_in_seconds } = poll || {}

  const checkPollEditAvailability = async () => {
    if (data && data.id && data.publications && data.publications.length !== 0) {
      try {
        const response = await request({
          path: `${ROUTE_MEDIA}/poll/${data.id}/availability`,
        })

        const { error, available } = response || {}

        if (!response || error) {
          Alert.error(error || ERROR_MESSAGE, { timeout: 5000 })
        } else {
          setCanPollBeEdited(available)
        }
      } catch (error) {
        errorHelper({ error, componentName: PollModal.displayName, functionName: 'checkPollEditAvailability' })
      } finally {
        setIsCheckingPollEditAvailability(false)
      }
    } else {
      setIsCheckingPollEditAvailability(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      if (data) {
        const poll = { ...data }

        poll[DURATION_IN_SECONDS] = data[DURATION]
        poll[DURATION] = transformSecondsToDuration({ [DURATION]: data[DURATION] })

        setPoll({ ...poll })
      } else {
        setPoll({
          ...{
            [TITLE]: postText || '',
            [OPTIONS]: [
              { position: 0, label: '' },
              { position: 0, label: '' },
            ],
            [DURATION]: transformSecondsToDuration({ [DURATION]: THREE_DAYS }),
            [DURATION_IN_SECONDS]: THREE_DAYS,
          },
        })
      }

      checkPollEditAvailability()
    }
  }, [isOpen])

  const createValidationSchema = () => {
    return Yup.object().shape({
      [TITLE]: Yup.string()
        .max(MAX_POLL_TITLE_LENGTH, `Poll title is too long - should be ${MAX_POLL_TITLE_LENGTH} chars maximum.`)
        .required('Post title is required.'),
      [DESCRIPTION]: Yup.string().max(
        MAX_POLL_DESCRIPTION_LENGTH,
        `Poll description is too long - should be ${MAX_POLL_DESCRIPTION_LENGTH} chars maximum.`
      ),
      [OPTIONS]: Yup.array().of(
        Yup.object().shape({
          position: Yup.number().required(),
          label: Yup.string()
            .max(
              MAX_POLL_OPTION_TEXT_LENGTH,
              `Choice text is too long - should be ${MAX_POLL_OPTION_TEXT_LENGTH} chars maximum.`
            )
            .required('Choice text is required.'),
        })
      ),
      [DURATION_IN_SECONDS]: Yup.number()
        .min(60, 'Poll duration should be at least 1 minute.')
        .required('Poll duration is required.')
        .typeError('Poll duration is required.'),
    })
  }

  const handleSubmitForm = async (values) => {
    try {
      const { selectedEntitiesWithSelectedProfiles, selectedEntitiesTemp } = getSelectedProfileGroupsAndTimezone({
        user,
      })

      let entities = []

      if (selectedEntitiesWithSelectedProfiles.length !== 0) {
        entities = selectedEntitiesWithSelectedProfiles
      } else if (selectedEntitiesTemp.length !== 0) {
        entities = selectedEntitiesTemp
      }

      const body = {
        [TITLE]: values[TITLE] || '',
        //  [DESCRIPTION]: values[DESCRIPTION] || '',
        [OPTIONS]: values[OPTIONS].map(({ label }, index) => ({ position: index, label })),
        [DURATION]: transformDurationToSeconds({
          duration: { [DAYS]: values[DAYS], [HOURS]: values[HOURS], [MINUTES]: values[MINUTES] },
        }),
      }

      if (!id) {
        body.entity_gids = entities.map(({ id }) => id)
      }

      if (body[DURATION] === 0) {
        Alert.error('Please check your poll length.', { timeout: 5000 })
        return
      }

      if (folderPath) {
        body.media_path = folderPath
      }

      setIsSavingPoll(true)

      let response

      if (id) {
        response = await request({
          path: `${ROUTE_MEDIA}/${id}`,
          method: 'PATCH',
          body,
        })
      } else {
        response = await request({
          path: `${ROUTE_MEDIA}/poll`,
          method: 'POST',
          body,
        })
      }

      setIsSavingPoll(false)

      const { error, data } = response || {}

      if (!response || error) {
        Alert.error(error || ERROR_MESSAGE, { timeout: 5000 })
      } else {
        Alert.success(id ? 'Poll has been saved.' : 'Poll has been created.', { timeout: 5000 })

        let removeMediaProcessingInterval = false

        if (!id || JSON.stringify(poll.options) !== JSON.stringify(body.options) || poll.title !== body.title) {
          removeMediaProcessingInterval = true
          delete data.thumbnail_url
          data.showPollProcessingClipLoader = true
        }

        handleUpdateMedias({ media: data, removeMediaProcessingInterval })

        handleDismiss()
      }
    } catch (error) {
      setIsSavingPoll(false)
      errorHelper({ error, componentName: PollModal.displayName, functionName: 'handleSubmitForm' })
    }
  }

  const handleSubmitFormFromButton = () => {
    formRef.current.handleSubmit()
  }

  const handleClickCloseModal = () => {
    if (!isSavingPoll) {
      handleDismiss()
    } else {
      Alert.error(`Please wait a bit longer while data is being processed.`, { timeout: 5000 })
    }
  }

  return (
    <StyledDialogOverlay isOpen={isOpen} onDismiss={() => {}}>
      <Box m="0 auto" width="100%" height="100%" p="l">
        <StyledDialogContent tabIndex="0">
          <StyledDialogEnvironmentWrapper px="m" justifyContent="space-between" alignItems="center" $isTop>
            <H4 my="m">{id ? 'Edit poll' : 'Create a new poll'}</H4>
          </StyledDialogEnvironmentWrapper>
          <Flex flexDirection="column" flex="1" position="relative">
            {isCheckingPollEditAvailability ? (
              <Flex
                alignItems="center"
                justifyContent="center"
                height="100%"
                width="100%"
                py="xxl"
                className="cliploader-wrapper"
              >
                <ClipLoader size="50" />
              </Flex>
            ) : (
              <Fragment>
                <Scrollbars universal autoHide>
                  {canPollBeEdited ? (
                    <Formik
                      initialValues={{
                        [TITLE]: title || '',
                        // [DESCRIPTION]: description || '',
                        [OPTIONS]: options || [],
                        [DURATION_IN_SECONDS]: duration_in_seconds || THREE_DAYS,
                        [DAYS]: duration?.days || 3,
                        [HOURS]: duration?.hours || 0,
                        [MINUTES]: duration?.minutes || 0,
                      }}
                      onSubmit={handleSubmitForm}
                      validationSchema={createValidationSchema}
                      autocomplete="off"
                      ref={formRef}
                      enableReinitialize
                    >
                      {({ values, handleChange, setFieldValue, errors, touched }) => {
                        const can_add_options = values[OPTIONS].length < MAX_POLL_OPTIONS

                        const has_duration_error =
                          errors[DURATION_IN_SECONDS] && touched[DURATION_IN_SECONDS] && errors[DURATION_IN_SECONDS]

                        return (
                          <Box px="m" mt="m">
                            <Box>
                              <Flex mb="xs" justifyContent="space-between" alignItems="center">
                                <Text fontSize="s">Title</Text>

                                <Counter counter={values[TITLE].length} max={MAX_POLL_TITLE_LENGTH} />
                              </Flex>

                              <Input
                                label=""
                                placeholder=""
                                value={values[TITLE]}
                                name={[TITLE]}
                                id={[TITLE]}
                                onChange={handleChange(TITLE)}
                                error={errors[TITLE] && touched[TITLE] && errors[TITLE]}
                                width="100%"
                              />
                            </Box>

                            <Box mt="m">
                              <FieldArray
                                name="options"
                                render={(arrayHelpers) => (
                                  <form autoComplete="off">
                                    <Flex flexDirection="column" width="100%">
                                      {values[OPTIONS].map((label, index) => (
                                        <Flex alignItems="center">
                                          <Field name={`${OPTIONS}.${index}`}>
                                            {({ field, form }) => {
                                              return (
                                                <StyledPollOptionWrapper key={index}>
                                                  {values[OPTIONS].length > MIN_POLL_OPTIONS && (
                                                    <NavigationLinkIconWrapper
                                                      onClick={() => {
                                                        arrayHelpers.remove(index)
                                                      }}
                                                    >
                                                      <Image width="8px" height="8px" src="/assets/clear.svg" />
                                                    </NavigationLinkIconWrapper>
                                                  )}

                                                  <Box>
                                                    <Flex mb="xs" justifyContent="space-between" alignItems="center">
                                                      <Text fontSize="s">Choice {index + 1}</Text>

                                                      <Counter
                                                        counter={(field.value.label || '').length}
                                                        max={MAX_POLL_OPTION_TEXT_LENGTH}
                                                      />
                                                    </Flex>

                                                    <Input
                                                      label=""
                                                      placeholder="Enter your choice"
                                                      type="text"
                                                      {...field}
                                                      value={field.value.label || ''}
                                                      onChange={(event) => {
                                                        setFieldValue(`${[OPTIONS]}.${index}.label`, event.target.value)
                                                      }}
                                                      error={
                                                        form.errors[OPTIONS] &&
                                                        form.touched[OPTIONS] &&
                                                        form.errors[OPTIONS][index] &&
                                                        form.errors[OPTIONS][index].label
                                                      }
                                                      width="100%"
                                                    />
                                                  </Box>
                                                </StyledPollOptionWrapper>
                                              )
                                            }}
                                          </Field>
                                        </Flex>
                                      ))}
                                    </Flex>

                                    {can_add_options && (
                                      <Flex justifyContent="flex-end">
                                        <StyledAddPollOptionWrapper
                                          alignItems="center"
                                          onClick={() => {
                                            if (can_add_options) {
                                              arrayHelpers.push({ position: values[OPTIONS].length, label: '' })
                                            }
                                          }}
                                        >
                                          <Image source="/assets/plus.svg" mr="xs" width="16px" height="16px" />
                                          <Text color="primary" fontSize="xs">
                                            Add another choice
                                          </Text>
                                        </StyledAddPollOptionWrapper>
                                      </Flex>
                                    )}
                                  </form>
                                )}
                              />
                            </Box>

                            <Box mt="m" pb="m">
                              <Flex flexDirection="column" width="100%">
                                <Flex mb="xs" alignItems="center">
                                  <Text pr="m">Poll duration</Text>

                                  <Tooltip
                                    onClick={() => {
                                      if (DURATION_HELP_LINK) {
                                        window.open(DURATION_HELP_LINK, '_blank')
                                      }
                                    }}
                                    wrapperComp={
                                      <Flex alignItems="center">
                                        <InfoIcon />
                                      </Flex>
                                    }
                                    message={`Duration of the poll.${
                                      DURATION_HELP_LINK ? ' Click to learn more.' : ''
                                    }`}
                                    width="285px"
                                    isTriangleVisible={false}
                                    left="0"
                                    right="unset"
                                    defaultTopOffset={10}
                                  />
                                </Flex>

                                <Grid gridTemplateColumns="repeat(3, 1fr)" gridGap="m" width="100%">
                                  <Flex flexDirection="column">
                                    <Text mb="xs" color="secondaryText" fontSize="xs">
                                      Days
                                    </Text>

                                    <StyledSelect
                                      name="days"
                                      onChange={({ target: { value } }) => {
                                        setFieldValue(DAYS, value)

                                        setFieldValue(
                                          DURATION_IN_SECONDS,
                                          transformDurationToSeconds({
                                            duration: {
                                              [DAYS]: value,
                                              [HOURS]: values[HOURS],
                                              [MINUTES]: values[MINUTES],
                                            },
                                          })
                                        )
                                      }}
                                      value={values[DAYS]}
                                    >
                                      {DAYS_OPTIONS.map((day) => (
                                        <option key={day} value={day}>
                                          {day}
                                        </option>
                                      ))}
                                    </StyledSelect>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Text mb="xs" color="secondaryText" fontSize="xs">
                                      Hours
                                    </Text>

                                    <StyledSelect
                                      name="hours"
                                      onChange={({ target: { value } }) => {
                                        setFieldValue(HOURS, value)

                                        setFieldValue(
                                          DURATION_IN_SECONDS,
                                          transformDurationToSeconds({
                                            duration: {
                                              [DAYS]: values[DAYS],
                                              [HOURS]: value,
                                              [MINUTES]: values[MINUTES],
                                            },
                                          })
                                        )
                                      }}
                                      value={values[HOURS]}
                                    >
                                      {HOURS_OPTIONS.map((hour) => (
                                        <option key={hour} value={hour}>
                                          {hour}
                                        </option>
                                      ))}
                                    </StyledSelect>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Text mb="xs" color="secondaryText" fontSize="xs">
                                      Minutes
                                    </Text>
                                    <StyledSelect
                                      name="minutes"
                                      onChange={({ target: { value } }) => {
                                        setFieldValue(MINUTES, value)

                                        setFieldValue(
                                          DURATION_IN_SECONDS,
                                          transformDurationToSeconds({
                                            duration: {
                                              [DAYS]: values[DAYS],
                                              [HOURS]: values[HOURS],
                                              [MINUTES]: value,
                                            },
                                          })
                                        )
                                      }}
                                      value={values[MINUTES]}
                                      $error={has_duration_error}
                                    >
                                      {MINUTES_OPTIONS.map((minute) => (
                                        <option key={minute} value={minute}>
                                          {minute}
                                        </option>
                                      ))}
                                    </StyledSelect>
                                  </Flex>
                                </Grid>

                                {has_duration_error && (
                                  <ErrorLabel mt="xxs" textAlign="left" error={has_duration_error} />
                                )}
                              </Flex>
                            </Box>

                            {/* <Box mt="m"> */}
                            {/*   <Text mb="xs" fontSize="s"> */}
                            {/*     Description */}
                            {/*   </Text> */}
                            {/*   <StyledTextArea */}
                            {/*     placeholder="" */}
                            {/*     label="" */}
                            {/*     value={values[DESCRIPTION]} */}
                            {/*     onChange={handleChange(DESCRIPTION)} */}
                            {/*     error={errors[DESCRIPTION] && touched[DESCRIPTION] && errors[DESCRIPTION]} */}
                            {/*     width="100%" */}
                            {/*     rows="4" */}
                            {/*   /> */}
                            {/* </Box> */}
                          </Box>
                        )
                      }}
                    </Formik>
                  ) : (
                    <Box px="m" mt="m">
                      <Text>Published polls can't be changed.</Text>

                      <Flex flexDirection="column" mt="m">
                        <Poll source={poll} isSmallView={false} />
                      </Flex>
                    </Box>
                  )}
                </Scrollbars>
              </Fragment>
            )}
          </Flex>
          <StyledDialogEnvironmentWrapper p="m" justifyContent="space-between" $isBottom>
            <Button.Gray isSmall onClick={handleClickCloseModal} mr="s">
              Cancel
            </Button.Gray>

            {canPollBeEdited ? (
              <ButtonWithLoading isSmall onClick={handleSubmitFormFromButton} type="submit" isLoading={isSavingPoll}>
                {isSavingPoll ? 'Saving' : 'Save'}
              </ButtonWithLoading>
            ) : (
              <Flex />
            )}
          </StyledDialogEnvironmentWrapper>
          <CloseIconWrapper className="modal-close-icon" onClick={handleClickCloseModal}>
            <Image width="10px" height="10px" src="/assets/clear.svg" />
          </CloseIconWrapper>
        </StyledDialogContent>
      </Box>
    </StyledDialogOverlay>
  )
}

PollModal.defaultProps = {
  data: null,
  postText: '',
  folderPath: '',
}

PollModal.propTypes = {
  user: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  handleDismiss: PropTypes.func.isRequired,
  data: PropTypes.object,
  postText: PropTypes.string,
  handleUpdateMedias: PropTypes.func.isRequired,
  folderPath: PropTypes.string,
}

PollModal.displayName = 'PollModal'

export default PollModal
